import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useGetStarted } from "@/components/GetStartedDialog";

export const BRAND_URL = "https://redkeyweb.design";
const WRAP = "mx-auto max-w-[1360px] px-6 md:px-10";

export function useRelatedWork(ids) {
  const [work, setWork] = useState([]);
  const key = (ids || []).join(",");
  useEffect(() => {
    if (!key) return;
    let mounted = true;
    api.get("/portfolio").then((r) => {
      if (!mounted) return;
      const map = new Map(r.data.map((x) => [x.id, x]));
      setWork(key.split(",").map((id) => map.get(id)).filter(Boolean));
    }).catch((e) => console.warn("portfolio load failed", e));
    return () => { mounted = false; };
  }, [key]);
  return work;
}

export function breadcrumbLd(items) {
  return {
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem", position: i + 1, name: it.name, item: `${BRAND_URL}${it.to}`,
    })),
  };
}

export function faqLd(faq) {
  return {
    "@type": "FAQPage",
    mainEntity: faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  };
}

export function Breadcrumbs({ items }) {
  return (
    <nav className={`${WRAP} pt-10`} aria-label="Breadcrumb">
      <ol className="flex flex-wrap items-center gap-2 font-ui text-[12px] tracking-widest uppercase text-label">
        {items.map((it, i) => (
          <li key={it.to} className="flex items-center gap-2">
            {i > 0 && <ChevronRight size={12} aria-hidden />}
            {i < items.length - 1
              ? <Link to={it.to} className="link-underline-grow">{it.name}</Link>
              : <span className="text-ink" aria-current="page">{it.name}</span>}
          </li>
        ))}
      </ol>
    </nav>
  );
}

export function LocalHero({ eyebrow, title, line, intro, prefill, testId }) {
  const { open } = useGetStarted();
  return (
    <section className={`${WRAP} pt-10 md:pt-14 pb-16`}>
      <p className="eyebrow">{eyebrow}</p>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-6 max-w-[22ch]" data-testid={`${testId}-h1`}>{title}</h1>
      <p className="mt-6 font-display text-xl md:text-2xl text-brand-red-ink leading-snug max-w-[40ch]">{line}</p>
      <p className="mt-6 max-w-prose text-lg text-ink-soft leading-relaxed">{intro}</p>
      <div className="mt-8 flex flex-wrap gap-3">
        <button type="button" onClick={() => open(prefill)} data-testid={`${testId}-hero-cta`}
          className="inline-flex items-center gap-3 bg-ink text-paper px-6 py-4 hover:bg-electric transition-colors duration-200 font-ui">
          Get a free project estimate <span aria-hidden>→</span>
        </button>
        <Link to="/audit" data-testid={`${testId}-audit-link`}
          className="inline-flex items-center gap-3 border hairline-strong px-6 py-4 hover:border-ink transition-colors duration-200 font-ui">
          Free site audit <ArrowRight size={16} />
        </Link>
      </div>
    </section>
  );
}

export function TextBand({ eyebrow, title, children, alt }) {
  return (
    <section className={`border-t hairline ${alt ? "bg-paper-alt" : ""}`}>
      <div className={`${WRAP} py-16 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-10`}>
        <div className="md:col-span-4">
          <p className="eyebrow">{eyebrow}</p>
          <h2 className="font-display text-3xl md:text-4xl mt-4">{title}</h2>
        </div>
        <div className="md:col-span-8 font-body text-lg text-ink-soft leading-relaxed space-y-6">{children}</div>
      </div>
    </section>
  );
}

export function BulletList({ items }) {
  return (
    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-3 text-ink">
          <span className="bg-brand-red mt-2.5 inline-block h-1.5 w-1.5 rounded-full shrink-0" aria-hidden />
          {item}
        </li>
      ))}
    </ul>
  );
}

export function LinkGrid({ eyebrow, title, links, testId, dark }) {
  return (
    <section className={`border-t hairline ${dark ? "bg-ink text-paper" : ""}`} data-testid={testId}>
      <div className={`${WRAP} py-16 md:py-24`}>
        <p className={`eyebrow ${dark ? "text-paper/60" : ""}`}>{eyebrow}</p>
        <h2 className={`font-display text-3xl md:text-4xl mt-4 ${dark ? "text-paper" : ""}`}>{title}</h2>
        <ul className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3">
          {links.map((l) => (
            <li key={l.to}>
              <Link to={l.to} data-testid={`${testId}-${l.to.split("/").pop()}`}
                className={`group flex items-center justify-between border p-4 font-ui transition-colors ${dark ? "border-paper/25 hover:border-brand-red" : "hairline-strong hover:border-ink"}`}>
                <span>{l.label}</span>
                <ArrowRight size={14} className="text-brand-red transition-transform group-hover:translate-x-1" aria-hidden />
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export function RelatedWork({ ids, testId }) {
  const work = useRelatedWork(ids);
  if (!work.length) return null;
  return (
    <section className="border-t hairline bg-paper-alt" data-testid={testId}>
      <div className={`${WRAP} py-16 md:py-24`}>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <p className="eyebrow">§ Proof</p>
            <h2 className="font-display text-3xl md:text-4xl mt-4">Real Southern California work</h2>
          </div>
          <Link to="/work" className="font-ui link-underline">See all work →</Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {work.map((w) => (
            <Link key={w.id} to={`/work/${w.id}`} className="portfolio-card group block" data-testid={`${testId}-${w.id}`}>
              <div className="aspect-[4/3] overflow-hidden bg-paper border hairline">
                <img src={w.image} alt={`${w.title} — case study cover`} loading="lazy" decoding="async" className="portfolio-img w-full h-full object-cover" />
              </div>
              <p className="eyebrow mt-3">{w.category}</p>
              <h3 className="font-display text-xl mt-1 group-hover:text-electric transition-colors">{w.title}</h3>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export function FaqList({ faq, testId }) {
  return (
    <section className="border-t hairline" data-testid={testId}>
      <div className={`${WRAP} py-16 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-10`}>
        <div className="md:col-span-4">
          <p className="eyebrow">§ FAQ</p>
          <h2 className="font-display text-3xl md:text-4xl mt-4">Common questions.</h2>
        </div>
        <dl className="md:col-span-8 divide-y divide-[hsl(var(--line-strong))] border-t border-b border-[hsl(var(--line-strong))]">
          {faq.map((f) => (
            <div key={f.q} className="py-6">
              <dt className="font-display text-xl md:text-2xl text-ink">{f.q}</dt>
              <dd className="mt-3 font-body text-ink-soft leading-relaxed max-w-prose">{f.a}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

export function CtaBand({ text, prefill, testId }) {
  const { open } = useGetStarted();
  return (
    <section className="border-t hairline">
      <div className={`${WRAP} py-14 md:py-20 flex flex-col md:flex-row md:items-center md:justify-between gap-6`}>
        <p className="font-display text-3xl md:text-4xl max-w-[26ch]">{text}</p>
        <button type="button" onClick={() => open(prefill)} data-testid={`${testId}-bottom-cta`}
          className="inline-flex items-center gap-3 bg-ink text-paper px-6 py-4 hover:bg-electric transition-colors duration-200 font-ui">
          Get Started <span aria-hidden>→</span>
        </button>
      </div>
    </section>
  );
}
